import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { get, post } from "../../util";
import { motion } from "framer-motion";
import { Grid } from "react-loader-spinner";
import TodayPredictionCard from "../modules/TodayPredictionCard";
import NextGameCard from "../modules/NextGameCard";
import PredictionCriteriaBox from "../modules/PredictionCriteriaBox";
import "./Predictions.css";
import moment from "moment-timezone";

const Predictions = ({ user }) => {
  const navigate = useNavigate();
  const [games, setGames] = useState<any[]>();
  const [predictions, setPredictions] = useState<any[]>();
  const [canEdit, setCanEdit] = useState(true);

  useEffect(() => {
    if (!user) {
      return navigate("/");
    }
    get("/api/todaygames").then((fetchedGames) => {
      setGames(fetchedGames.games);
      get("/api/gettodaypredictions").then((res) => {
        if (!res || !res.length) {
          setPredictions(
            fetchedGames.games.map((g) => ({
              gameId: g._id,
              margin: undefined,
              winner: undefined,
              date: g.date,
            }))
          );
        } else {
          setPredictions(res);
        }
      });
      if (fetchedGames && fetchedGames.games.length) {
        const earliestGame = fetchedGames.games[0];
        const earliestTime = moment(earliestGame.time, "h:mm A").format(
          "HH:mm:ss"
        );
        const now = moment().format("HH:mm:ss");
        if (now > earliestTime) setCanEdit(false);
      }
    });
  }, [user, navigate]);

  const savePredictions = () => {
    if (!checkFilled())
      return window.alert("Please fill in all your predictions!");
    post("/api/setpredictions", { predictions }).then((res) => {
      window.alert(res ? "Predictions Saved!" : "Error saving predictions");
    });
  };

  const setMargins = (margin: number, index: number) => {
    const newPredictions = predictions;
    newPredictions[index].margin = margin;
    setPredictions(newPredictions);
  };

  const setWinners = (winner: string, index: number) => {
    const newPredictions = predictions;
    newPredictions[index].winner = winner;
    setPredictions(newPredictions);
  };

  const checkFilled = () => {
    for (const p of predictions) {
      if (!p.winner || !p.margin) return false;
    }
    return true;
  };

  if (!games || !predictions)
    return (
      <div className="bg">
        <div className="center-screen">
          <Grid color="black" height={50} width={50} />
        </div>
      </div>
    );

  if (!games.length) return <NoGamesToday />;

  const gamesList = games.map((game) => (
    <NextGameCard home={game.home} away={game.away} time={game.time} />
  ));

  const predictionCards = [];

  for (const prediction of predictions) {
    const game = games.filter((g) => g._id === prediction.gameId)[0];
    if (!game) {
      continue;
    }
    predictionCards.push(
      <TodayPredictionCard
        away={game.away}
        home={game.home}
        startTime={game.time}
        predictedWinner={prediction.winner}
        predictedMargin={prediction.margin}
      />
    );
  }

  if (!canEdit)
    return <PastEditTime gamesList={gamesList} predictions={predictionCards} />;

  const predictionCritList = [];
  for (let i = 0; i < games.length; i++) {
    const game = games[i];
    const pred = predictions.filter((p) => p.gameId === game._id)[0];
    predictionCritList.push(
      <PredictionCriteriaBox
        home={game.home}
        away={game.away}
        winner={pred ? pred.winner : undefined}
        margin={pred ? pred.margin : undefined}
        setWinner={setWinners}
        setMargin={setMargins}
        index={i}
      />
    );
  }

  //list of items that are combination of the matchup and the input boxes
  let gameEntryVisualList = [];
  for (let i = 0; i < gamesList.length; i++) {
    gameEntryVisualList.push(
      <div className="Predictions-item">
        {gamesList[i]}
        {predictionCritList[i]}
      </div>
    );
  }

  return (
    <>
      <div className="bg">
        <div className="Predictions-header">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { scale: 0.5, opacity: 0 },
              visible: { scale: 1, opacity: 1, transition: { delay: 0.2 } },
            }}
          >
            <header className="Predictions-headerText">Prediction Entry</header>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { scale: 0.5, opacity: 0 },
              visible: { scale: 1, opacity: 1, transition: { delay: 0.32 } },
            }}
          >
            <h3 className="Predictions-headersubText"> It's GAMEDAY!</h3>
            <h3 className="Predictions-headersubText">
              {" "}
              Lock in your predictions below.
            </h3>
          </motion.div>
        </div>

        <>
          {games.length ? (
            <h1 className="u-textCenter">
              Enter Predictions before{" "}
              {moment(games[0].time, "HH:mm").format("hh:mm A")}
            </h1>
          ) : (
            <h1 className="u-textCenter">No Games Today!</h1>
          )}
          <div className="NextGameCard-allGamesContainer">
            {" "}
            {gameEntryVisualList}
          </div>
          <div className="Predictions-buttonContainer">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={savePredictions}
              className="Predictions-submitButton"
            >
              Save
            </motion.button>
          </div>
        </>
      </div>
    </>
  );
};

const PastEditTime = ({ gamesList, predictions }) =>
  predictions && predictions.length ? (
    <>
      <div className="bg">
        <div className="Predictions-header">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { scale: 0.8, opacity: 0 },
              visible: {
                scale: 1,
                opacity: 1,
                transition: { delay: 0.08, duration: 0.1 },
              },
            }}
          >
            <header className="Predictions-headerText">Locked In</header>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { scale: 0.8, opacity: 0 },
              visible: {
                scale: 1,
                opacity: 1,
                transition: { delay: 0.08, duration: 0.1 },
              },
            }}
          >
            <h3 className="Predictions-headersubText">
              You've locked in your predictions for today.
            </h3>
            <h3 className="Predictions-headersubText">View them below.</h3>
          </motion.div>
        </div>
        <div className="NextGameCard-allGamesContainer">{predictions}</div>
      </div>
    </>
  ) : (
    <>
      <div className="bg">
        <div className="OOPS-header">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { scale: 0.8, opacity: 0 },
              visible: {
                scale: 1,
                opacity: 1,
                transition: { delay: 0.08, duration: 0.1 },
              },
            }}
          >
            <header className="Predictions-headerText">oopsies!</header>
          </motion.div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { scale: 0.8, opacity: 0 },
              visible: {
                scale: 1,
                opacity: 1,
                transition: { delay: 0.08, duration: 0.1 },
              },
            }}
          >
            <h3 className="Predictions-headersubText">
              It's too late to submit predictions!
            </h3>
          </motion.div>
        </div>
        <div className="NextGameCard-allGamesContainer">{gamesList}</div>
      </div>
    </>
  );

const NoGamesToday = () => (
  <>
    <div className="bg">
      <div className="OOPS-header">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { scale: 0.8, opacity: 0 },
            visible: {
              scale: 1,
              opacity: 1,
              transition: { delay: 0.08, duration: 0.1 },
            },
          }}
        >
          <header className="Predictions-headerText">Oh no!</header>
        </motion.div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { scale: 0.8, opacity: 0 },
            visible: {
              scale: 1,
              opacity: 1,
              transition: { delay: 0.08, duration: 0.1 },
            },
          }}
        >
          <h3 className="Predictions-headersubText">
            There are no NBA games today!
          </h3>
        </motion.div>
      </div>
    </div>
  </>
);

export default Predictions;
